import React, { useState, useEffect, useCallback } from 'react';
import { Question, MultipleChoiceQuestion, TrueFalseQuestion, ShortAnswerQuestion, BooleanQuestion } from '@/modules/lms/modules/components/types';

// Interface for backend question format
interface BackendQuestion {
  question_id: string;
  question_text: string;
  question_type: string;
  correct_answer?: string | boolean;
  options?: string[];
  points?: number;
}

// Function to map backend question format to frontend format
const mapBackendToFrontendQuestion = (backendQuestion: BackendQuestion): Question => {
  const baseQuestion = {
    id: backendQuestion.question_id,
    text: backendQuestion.question_text,
    points: backendQuestion.points || 1
  };

  switch (backendQuestion.question_type) {
    case 'multiple_choice':
      const options = (backendQuestion.options || []).map((option, index) => ({
        id: `opt_${index}`,
        text: option,
        isCorrect: false // Default to false, will be set based on correct_answer
      }));
      
      // If correct_answer is provided and it's a string, try to match it with option text
      if (backendQuestion.correct_answer && typeof backendQuestion.correct_answer === 'string') {
        const correctIndex = options.findIndex(opt => opt.text === backendQuestion.correct_answer);
        if (correctIndex !== -1) {
          options[correctIndex].isCorrect = true;
        }
      }
      
      return {
        ...baseQuestion,
        type: 'multiple_choice',
        options
      } as MultipleChoiceQuestion;

    case 'boolean':
    case 'true_false':
      return {
        ...baseQuestion,
        type: backendQuestion.question_type === 'boolean' ? 'boolean' : 'true_false',
        correctAnswer: backendQuestion.correct_answer === 'true' || backendQuestion.correct_answer === true
      } as TrueFalseQuestion | BooleanQuestion;

    case 'short_answer':
      return {
        ...baseQuestion,
        type: 'short_answer',
        correctAnswer: backendQuestion.correct_answer as string || ''
      } as ShortAnswerQuestion;

    default:
      // Fallback to short_answer for unknown types
      return {
        ...baseQuestion,
        type: 'short_answer',
        correctAnswer: backendQuestion.correct_answer as string || ''
      } as ShortAnswerQuestion;
  }
};

interface QuizContentRendererProps {
  payload: {
    questions: Question[] | BackendQuestion[];
    title?: string;
    description?: string;
    time_limit?: number; // in minutes
    passing_score?: number; // percentage
    allow_retake?: boolean;
    show_correct_answers?: boolean;
  };
}

interface QuizState {
  isStarted: boolean;
  currentQuestionIndex: number;
  answers: { [questionId: string]: any };
  timeRemaining: number;
  isSubmitted: boolean;
  results: {
    score: number;
    total: number;
    percentage: number;
    passed: boolean;
    correctAnswers: { [questionId: string]: any };
    userAnswers: { [questionId: string]: any };
  } | null;
}

export const QuizContentRenderer: React.FC<QuizContentRendererProps> = ({ payload }) => {
  const {
    questions: rawQuestions = [],
    title = "Bài kiểm tra",
    description = "",
    time_limit = 0,
    passing_score = 60,
    allow_retake = true,
    show_correct_answers = true
  } = payload;

  // Map backend questions to frontend format if needed
  const questions: Question[] = React.useMemo(() => {
    if (rawQuestions.length === 0) return [];
    
    // Check if it's already in frontend format (has 'id' and 'type' properties)
    const isFrontendFormat = rawQuestions[0] && 'id' in rawQuestions[0] && 'type' in rawQuestions[0];
    
    if (isFrontendFormat) {
      return rawQuestions as Question[];
    } else {
      // Map from backend format to frontend format
      return (rawQuestions as BackendQuestion[]).map(mapBackendToFrontendQuestion);
    }
  }, [rawQuestions]);

  const [quizState, setQuizState] = useState<QuizState>({
    isStarted: false,
    currentQuestionIndex: 0,
    answers: {},
    timeRemaining: time_limit * 60, // convert to seconds
    isSubmitted: false,
    results: null
  });

  const [showSubmitWarning, setShowSubmitWarning] = useState(false);

  // Debug logging
  useEffect(() => {
    
  }, [quizState]);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (quizState.isStarted && !quizState.isSubmitted && time_limit > 0) {
      interval = setInterval(() => {
        setQuizState(prev => {
          if (prev.timeRemaining <= 1) {
            // Time's up - auto submit
            return { ...prev, timeRemaining: 0, isSubmitted: true };
          }
          return { ...prev, timeRemaining: prev.timeRemaining - 1 };
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [quizState.isStarted, quizState.isSubmitted, time_limit]);

  // Quiz functions
  const submitQuiz = useCallback(() => {
    // Prevent submission if already submitted
    if (quizState.isSubmitted) {
      
      return;
    }

    // Check if quiz is started
    if (!quizState.isStarted) {
      
      return;
    }

    
    
    let score = 0;
    const correctAnswers: { [questionId: string]: any } = {};
    const userAnswers = { ...quizState.answers };

    questions.forEach((question) => {
      const userAnswer = quizState.answers[question.id];
      let isCorrect = false;

      if (question.type === 'multiple_choice') {
        const mcQuestion = question as MultipleChoiceQuestion;
        const correctOption = mcQuestion.options.find(opt => opt.isCorrect);
        isCorrect = userAnswer === correctOption?.id;
        correctAnswers[question.id] = correctOption?.id;
      } else if (question.type === 'true_false' || question.type === 'boolean') {
        const tfQuestion = question as TrueFalseQuestion;
        isCorrect = userAnswer === tfQuestion.correctAnswer;
        correctAnswers[question.id] = tfQuestion.correctAnswer;
      } else if (question.type === 'short_answer') {
        const saQuestion = question as ShortAnswerQuestion;
        isCorrect = userAnswer?.toLowerCase().trim() === saQuestion.correctAnswer.toLowerCase().trim();
        correctAnswers[question.id] = saQuestion.correctAnswer;
      }

      if (isCorrect) {
        score += question.points || 1;
      }
    });

    const total = questions.reduce((sum, q) => sum + (q.points || 1), 0);
    const percentage = Math.round((score / total) * 100);
    const passed = percentage >= passing_score;

    setQuizState(prev => ({
      ...prev,
      isSubmitted: true,
      results: {
        score,
        total,
        percentage,
        passed,
        correctAnswers,
        userAnswers
      }
    }));
  }, [quizState.answers, quizState.isSubmitted, quizState.isStarted, questions, passing_score]);

  const startQuiz = () => {
    
    
    setQuizState({
      isStarted: true,
      currentQuestionIndex: 0,
      answers: {},
      timeRemaining: time_limit * 60,
      isSubmitted: false,
      results: null
    });
  };

  const handleAnswerChange = (questionId: string, answer: any) => {
    setQuizState(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: answer
      }
    }));
  };


  const goToQuestion = (index: number) => {
    if (index >= 0 && index < questions.length) {
      setQuizState(prev => ({
        ...prev,
        currentQuestionIndex: index
      }));
    }
  };

  // Check if all questions have been answered
  const areAllQuestionsAnswered = useCallback(() => {
    return questions.every(question => 
      quizState.answers[question.id] !== undefined && 
      quizState.answers[question.id] !== null &&
      quizState.answers[question.id] !== ''
    );
  }, [questions, quizState.answers]);

  const nextQuestion = () => {
    if (quizState.currentQuestionIndex < questions.length - 1) {
      goToQuestion(quizState.currentQuestionIndex + 1);
    } else {
      // Only submit if we're on the last question and ALL questions are answered
      if (areAllQuestionsAnswered()) {
        submitQuiz();
      } else {
        // If not all questions are answered, show a warning
        setShowSubmitWarning(true);
        setTimeout(() => setShowSubmitWarning(false), 3000);
      }
    }
  };

  const previousQuestion = () => {
    if (quizState.currentQuestionIndex > 0) {
      goToQuestion(quizState.currentQuestionIndex - 1);
    }
  };

  const resetQuiz = () => {
    setQuizState({
      isStarted: false,
      currentQuestionIndex: 0,
      answers: {},
      timeRemaining: time_limit * 60,
      isSubmitted: false,
      results: null
    });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Wrap the main render logic in try-catch

  try {
    // Show results if quiz is completed
    if (quizState.results) {
    const { results } = quizState;
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className={`text-6xl mb-4 ${results.passed ? 'text-green-500' : 'text-red-500'}`}>
            {results.passed ? '🎉' : '😞'}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {results.passed ? 'Chúc mừng! Bạn đã vượt qua bài kiểm tra' : 'Bạn chưa vượt qua bài kiểm tra'}
          </h2>
          <div className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            {title}
          </div>
        </div>

        {/* Score Summary */}
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {results.score}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Điểm số</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                {results.percentage}%
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Tỷ lệ đúng</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {results.total}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Tổng điểm</div>
            </div>
          </div>
        </div>

        {/* Detailed Results */}
        {show_correct_answers && (
          <div className="space-y-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Chi tiết kết quả
            </h3>
            {questions.map((question, index) => {
              const userAnswer = results.userAnswers[question.id];
              const correctAnswer = results.correctAnswers[question.id];
              const isCorrect = userAnswer === correctAnswer;

              return (
                <div key={question.id} className="bg-white dark:bg-gray-700 rounded-lg p-4 border">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      Câu {index + 1}: {question.text}
                    </h4>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      isCorrect 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {isCorrect ? 'Đúng' : 'Sai'}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Câu trả lời của bạn: 
                      </span>
                      <span className={`ml-2 ${isCorrect ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {question.type === 'multiple_choice' 
                          ? (question as MultipleChoiceQuestion).options.find(opt => opt.id === userAnswer)?.text || 'Chưa trả lời'
                          : question.type === 'true_false' || question.type === 'boolean'
                          ? userAnswer ? 'Đúng' : 'Sai'
                          : userAnswer || 'Chưa trả lời'
                        }
                      </span>
                    </div>
                    
                    {!isCorrect && (
                      <div>
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Đáp án đúng: 
                        </span>
                        <span className="ml-2 text-green-600 dark:text-green-400">
                          {question.type === 'multiple_choice' 
                            ? (question as MultipleChoiceQuestion).options.find(opt => opt.id === correctAnswer)?.text
                            : question.type === 'true_false' || question.type === 'boolean'
                            ? correctAnswer ? 'Đúng' : 'Sai'
                            : correctAnswer
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-center space-x-4">
          {allow_retake && (
            <button
              onClick={resetQuiz}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Làm lại
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show quiz questions if started
  if (quizState.isStarted) {
    
    
    // Check if there are questions
    if (questions.length === 0) {
      return (
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded-lg p-6">
            <div className="text-red-600 dark:text-red-400 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              Không có câu hỏi
            </h3>
            <p className="text-red-600 dark:text-red-400 mb-4">
              Bài kiểm tra này chưa có câu hỏi nào. Vui lòng liên hệ với giáo viên.
            </p>
            <button
              onClick={resetQuiz}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Quay lại
            </button>
          </div>
        </div>
      );
    }

    const currentQuestion = questions[quizState.currentQuestionIndex];
    
    
    
    if (!currentQuestion) {
      return (
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded-lg p-6">
            <div className="text-red-600 dark:text-red-400 text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
              Lỗi câu hỏi
            </h3>
            <p className="text-red-600 dark:text-red-400 mb-4">
              Không thể tải câu hỏi hiện tại. Vui lòng thử lại.
            </p>
            <button
              onClick={resetQuiz}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Quay lại
            </button>
          </div>
        </div>
      );
    }
    
    const userAnswer = quizState.answers[currentQuestion.id];

    const renderQuestion = () => {
      try {
        
        switch (currentQuestion.type) {
        case 'multiple_choice':
          const mcQuestion = currentQuestion as MultipleChoiceQuestion;
          return (
            <div className="space-y-3">
              {mcQuestion.options.map((option) => (
                <label
                  key={option.id}
                  className={`flex items-center p-4 rounded-lg border cursor-pointer transition-colors ${
                    userAnswer === option.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion.id}`}
                    value={option.id}
                    checked={userAnswer === option.id}
                    onChange={() => handleAnswerChange(currentQuestion.id, option.id)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                    userAnswer === option.id
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {userAnswer === option.id && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="text-gray-900 dark:text-gray-100 flex-1">{option.text}</span>
                </label>
              ))}
            </div>
          );

        case 'true_false':
        case 'boolean':
          return (
            <div className="space-y-3">
              {[
                { value: true, label: 'Đúng' },
                { value: false, label: 'Sai' }
              ].map((option) => (
                <label
                  key={option.value.toString()}
                  className={`flex items-center p-4 rounded-lg border cursor-pointer transition-colors ${
                    userAnswer === option.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion.id}`}
                    value={option.value.toString()}
                    checked={userAnswer === option.value}
                    onChange={() => handleAnswerChange(currentQuestion.id, option.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                    userAnswer === option.value
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}>
                    {userAnswer === option.value && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                  <span className="text-gray-900 dark:text-gray-100">{option.label}</span>
                </label>
              ))}
            </div>
          );

        case 'short_answer':
          return (
            <div>
              <textarea
                value={userAnswer || ''}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                placeholder="Nhập câu trả lời của bạn..."
                className="w-full p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={4}
              />
            </div>
          );

        default:
          return <div>Loại câu hỏi không được hỗ trợ</div>;
        }
      } catch (error) {
        console.error('Error rendering question:', error);
        return (
          <div className="bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded-lg p-4">
            <div className="text-red-600 dark:text-red-400 text-sm">
              Lỗi hiển thị câu hỏi: {error instanceof Error ? error.message : 'Unknown error'}
            </div>
          </div>
        );
      }
    };

    return (
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h2>
            {time_limit > 0 && (
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                quizState.timeRemaining < 60 
                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
              }`}>
                ⏰ {formatTime(quizState.timeRemaining)}
              </div>
            )}
          </div>
          
          {description && (
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {description}
            </p>
          )}

          {/* Progress */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Câu {quizState.currentQuestionIndex + 1} / {questions.length}
            </span>
            <div className="w-48 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((quizState.currentQuestionIndex + 1) / questions.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Submit Warning */}
        {showSubmitWarning && (
          <div className="bg-orange-100 dark:bg-orange-900 border border-orange-300 dark:border-orange-700 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-orange-800 dark:text-orange-200 font-medium">
                Bạn cần trả lời hết tất cả {questions.length} câu hỏi trước khi có thể nộp bài. Hiện tại đã trả lời {Object.keys(quizState.answers).length} câu.
              </span>
            </div>
          </div>
        )}

        {/* Question Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {questions.map((_, index) => (
              <button
                key={index}
                onClick={() => goToQuestion(index)}
                className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                  index === quizState.currentQuestionIndex
                    ? 'bg-blue-600 text-white'
                    : quizState.answers[questions[index].id] !== undefined
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Question */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex-1">
              {currentQuestion.text}
            </h3>
            <div className="ml-4 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-600 dark:text-gray-400 rounded">
              {currentQuestion.points || 1} điểm
            </div>
          </div>

          {renderQuestion()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={previousQuestion}
            disabled={quizState.currentQuestionIndex === 0}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              quizState.currentQuestionIndex === 0
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
            }`}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Trước
          </button>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            {Object.keys(quizState.answers).length} / {questions.length} câu đã trả lời
            {quizState.currentQuestionIndex === questions.length - 1 && !areAllQuestionsAnswered() && (
              <span className="ml-2 text-orange-600 dark:text-orange-400 font-medium">
                (Cần trả lời hết để nộp bài)
              </span>
            )}
          </div>

          <button
            onClick={nextQuestion}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              quizState.currentQuestionIndex === questions.length - 1
                ? areAllQuestionsAnswered()
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
            disabled={quizState.currentQuestionIndex === questions.length - 1 && !areAllQuestionsAnswered()}
          >
            {quizState.currentQuestionIndex === questions.length - 1 ? 'Nộp bài' : 'Tiếp'}
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Show quiz start screen
  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 shadow-sm">
        <div className="text-center">
          <div className="text-6xl mb-6">📝</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {title}
          </h2>
          
          {description && (
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {description}
            </p>
          )}

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-gray-600 dark:text-gray-400">
                  {questions.length} câu hỏi
                </span>
              </div>
              
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-600 dark:text-gray-400">
                  {time_limit > 0 ? `${time_limit} phút` : 'Không giới hạn thời gian'}
                </span>
              </div>
              
              <div className="flex items-center">
                <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-600 dark:text-gray-400">
                  Điểm tối thiểu: {passing_score}%
                </span>
              </div>
              
              <div className="flex items-center">
                <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-600 dark:text-gray-400">
                  Tổng điểm: {questions.reduce((sum, q) => sum + (q.points || 1), 0)}
                </span>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Bạn có thể điều hướng giữa các câu hỏi và thay đổi câu trả lời bất kỳ lúc nào trước khi nộp bài.
          </div>

          <button
            onClick={startQuiz}
            className="inline-flex items-center px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-lg font-medium"
          >
            <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Bắt đầu kiểm tra
          </button>
        </div>
      </div>
    </div>
  );
  } catch (error) {
    console.error('Error in QuizContentRenderer:', error);
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 rounded-lg p-6">
          <div className="text-red-600 dark:text-red-400 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
            Lỗi hiển thị bài kiểm tra
          </h3>
          <p className="text-red-600 dark:text-red-400 mb-4">
            {error instanceof Error ? error.message : 'Có lỗi xảy ra khi tải bài kiểm tra'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Tải lại trang
          </button>
        </div>
      </div>
    );
  }
};
